import React, { useRef, useEffect, useState, useCallback } from 'react';

const GLOBAL_TIME_PENALTY_DEFAULTS = [
  { hours: 0, minutes: 0, value: 5.5 },
  { hours: 6, minutes: 0, value: 0.5 },
  { hours: 7, minutes: 0, value: 0.05 },
  { hours: 7, minutes: 10, value: 0.0 },
  { hours: 8, minutes: 0, value: 0.1 },
  { hours: 9, minutes: 0, value: 5.5 },
  { hours: 10, minutes: 0, value: 5.5 },
  { hours: 11, minutes: 0, value: 2.2 },
  { hours: 12, minutes: 0, value: 1.7 },
  { hours: 12, minutes: 45, value: 1.25 },
  { hours: 13, minutes: 0, value: 1.7 },
  { hours: 13, minutes: 30, value: 2.45 },
  { hours: 14, minutes: 0, value: 5.5 },
  { hours: 23, minutes: 59, value: 5.5 },
];

const timeToMinutes = (hours, minutes) => hours * 60 + minutes;

const minutesToTimeLabel = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
};

function TimePenaltyChart({ value, onChange, defaults }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const dragPointRef = useRef(null);
  const panRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const W = 720;
  const H = 280;
  const padT = 20;
  const padB = 35;
  const padL = 50;
  const padR = 20;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxMinutes = 24 * 60;
  const maxPenalty = 6;

  const toX = (totalMin) => padL + (totalMin / maxMinutes) * innerW;
  const toY = (val) => padT + innerH - (val / maxPenalty) * innerH;

  const getX = (p) => toX(timeToMinutes(p.hours, p.minutes));
  const getY = (p) => toY(p.value);

  const sorted = [...value].sort((a, b) => timeToMinutes(a.hours, a.minutes) - timeToMinutes(b.hours, b.minutes));

  let pathD = '';
  if (sorted.length >= 2) {
    pathD = sorted.map((p, i) => {
      const cmd = i === 0 ? 'M' : 'L';
      return cmd + ' ' + getX(p) + ' ' + getY(p);
    }).join(' ');
  }

  const getSvgCoords = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (W / rect.width),
      y: (e.clientY - rect.top) * (H / rect.height)
    };
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (!svgRef.current) return;
    const coords = getSvgCoords(e);
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const newZoom = Math.max(0.5, Math.min(6, zoom * factor));
    const scale = newZoom / zoom;
    setPanX(panX + coords.x * (1 - scale));
    setPanY(panY + coords.y * (1 - scale));
    setZoom(newZoom);
  }, [zoom, panX, panY, getSvgCoords]);

  const handleSvgMouseDown = (e) => {
    if (e.button !== 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const rawX = (e.clientX - rect.left) * (W / rect.width);
    const rawY = (e.clientY - rect.top) * (H / rect.height);
    const svgX = (rawX - panX) / zoom;
    const svgY = (rawY - panY) / zoom;

    // Check if clicking near a point (same threshold as hover)
    const hitThreshold = 10;
    for (let i = 0; i < value.length; i++) {
      const px = getX(value[i]);
      const py = getY(value[i]);
      const ddx = svgX - px;
      const ddy = svgY - py;
      if (ddx * ddx + ddy * ddy < hitThreshold * hitThreshold) {
        e.preventDefault();
        e.stopPropagation();
        dragPointRef.current = {
          idx: i,
          value: { hours: value[i].hours, minutes: value[i].minutes, value: value[i].value }
        };
        return;
      }
    }

    // Not on a point — start pan if zoomed
    if (zoom > 1.1) {
      e.preventDefault();
      panRef.current = { clientX: e.clientX, clientY: e.clientY, panX, panY };
    }
  };

  const handlePointerMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    // Raw SVG coords before transform
    const rawX = (e.clientX - rect.left) * (W / rect.width);
    const rawY = (e.clientY - rect.top) * (H / rect.height);
    // Reverse the zoom/pan transform to get data-space coords
    const svgX = (rawX - panX) / zoom;
    const svgY = (rawY - panY) / zoom;

    if (dragPointRef.current) {
      const totalMin = ((svgX - padL) / innerW) * maxMinutes;
      const normX = Math.max(0, Math.min(maxMinutes, totalMin));
      const newH = Math.floor(normX / 60) % 24;
      const newM = Math.round(normX) % 60;
      const normPenalty = ((padT + innerH - svgY) / innerH) * maxPenalty;
      const newVal = Math.max(0, Math.min(maxPenalty, Math.round(normPenalty * 100) / 100));
      const updated = [...value];
      updated[dragPointRef.current.idx] = { hours: newH, minutes: newM, value: newVal };
      onChange(updated);
      return;
    }
    if (panRef.current) {
      const scaleX = W / rect.width;
      const scaleY = H / rect.height;
      const dx = (e.clientX - panRef.current.clientX) * scaleX;
      const dy = (e.clientY - panRef.current.clientY) * scaleY;
      setPanX(panRef.current.panX + dx);
      setPanY(panRef.current.panY + dy);
      return;
    }

    // Hover detection using exact same coords as drag
    const hitThreshold = 10;
    let foundIdx = null;
    for (let i = 0; i < value.length; i++) {
      const px = getX(value[i]);
      const py = getY(value[i]);
      const ddx = svgX - px;
      const ddy = svgY - py;
      if (ddx * ddx + ddy * ddy < hitThreshold * hitThreshold) {
        foundIdx = i;
        break;
      }
    }
    if (foundIdx !== hoveredIdx) setHoveredIdx(foundIdx);
  };

  const handlePointerUp = () => {
    dragPointRef.current = null;
    panRef.current = null;
  };

  const handleDoubleClick = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const rawX = (e.clientX - rect.left) * (W / rect.width);
    const rawY = (e.clientY - rect.top) * (H / rect.height);
    const svgX = (rawX - panX) / zoom;
    const svgY = (rawY - panY) / zoom;

    // Check if double-clicked near an existing point (within ~15px in data space)
    const hitThreshold = 10;
    for (let i = 0; i < value.length; i++) {
      const px = getX(value[i]);
      const py = getY(value[i]);
      const dx = svgX - px;
      const dy = svgY - py;
      if (dx * dx + dy * dy < hitThreshold * hitThreshold) {
        // Delete this point
        deletePoint(i);
        return;
      }
    }

    // Otherwise add a new point at clicked position
    if (svgX >= padL && svgX <= W - padR && svgY >= padT && svgY <= H - padB) {
      const totalMin = ((svgX - padL) / innerW) * maxMinutes;
      const normX = Math.max(0, Math.min(maxMinutes, totalMin));
      const newH = Math.floor(normX / 60) % 24;
      const newM = Math.round(normX) % 60;
      const normPenalty = ((padT + innerH - svgY) / innerH) * maxPenalty;
      const newVal = Math.max(0, Math.min(maxPenalty, Math.round(normPenalty * 100) / 100));
      onChange([...value, { hours: newH, minutes: newM, value: newVal }]);
    }
  };

  useEffect(() => {
    const handler = () => { handlePointerUp(); };
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheelCapture = (e) => e.preventDefault();
    el.addEventListener('wheel', onWheelCapture, { passive: false });
    return () => el.removeEventListener('wheel', onWheelCapture);
  }, []);

  const handleResetView = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };

  const deletePoint = (idx) => {
    if (value.length <= 2) return;
    onChange(value.filter((item, i) => i !== idx));
  };

  const addPoint = () => {
    onChange([...value, { hours: 12, minutes: 0, value: 3.0 }]);
  };

  const resetToDefaults = () => {
    onChange([...defaults]);
  };

  const gridHours = [0, 3, 6, 9, 12, 15, 18, 21, 24];
  const gridPenalties = [0, 1, 2, 3, 4, 5, 6];

  const renderGrid = () => {
    const lines = [];
    gridPenalties.forEach((v) => {
      lines.push(
        <line key={'gy' + v} x1={padL} y1={toY(v)} x2={W - padR} y2={toY(v)} stroke="#e0e0e0" strokeWidth={1} />
      );
    });
    gridHours.forEach((h) => {
      lines.push(
        <line key={'gx' + h} x1={toX(h * 60)} y1={padT} x2={toX(h * 60)} y2={H - padB} stroke="#e0e0e0" strokeWidth={1} />
      );
    });
    return lines;
  };

  const renderAxisLabels = () => {
    const labels = [];
    gridPenalties.forEach((v) => {
      labels.push(
        <text key={'yl' + v} x={padL - 8} y={toY(v) + 4} textAnchor="end" fontSize={11} fill="#666">{v}</text>
      );
    });
    gridHours.forEach((h) => {
      labels.push(
        <text key={'xl' + h} x={toX(h * 60)} y={H - padB + 18} textAnchor="middle" fontSize={11} fill="#666">{h + ':00'}</text>
      );
    });
    labels.push(
      <text key="ylbl" x={12} y={padT + innerH / 2} textAnchor="middle" fontSize={12} fill="#333"
        transform={'rotate(-90, 12, ' + (padT + innerH / 2) + ')'}>Penalty</text>
    );
    labels.push(
      <text key="xlabel" x={padL + innerW / 2} y={H - 2} textAnchor="middle" fontSize={12} fill="#333">Time</text>
    );
    return labels;
  };

  const renderPoints = () => {
    return value.map((p, i) => {
      const px = getX(p);
      const py = getY(p);
      const timeLabel = minutesToTimeLabel(timeToMinutes(p.hours, p.minutes));
      const valLabel = p.value.toFixed(2);
      const isHovered = hoveredIdx === i;
      const radius = isHovered ? 5 : 4;
      const fill = isHovered ? '#ff6600' : '#007bff';
      return (
        <g key={i}>
          <circle cx={px} cy={py} r={20} fill="transparent" style={{ cursor: 'grab' }} />
          <circle cx={px} cy={py} r={radius} fill={fill} stroke="#fff" strokeWidth={2}
            style={{ cursor: 'grab', transition: 'all 0.15s' }} />
          <text x={px} y={py - 12} textAnchor="middle" fontSize={10} fill="#333" pointerEvents="none">
            {timeLabel} ({valLabel})
          </text>
        </g>
      );
    });
  };

  const transform = 'translate(' + panX + ', ' + panY + ') scale(' + zoom + ')';

  return (
    <div className="time-penalty-chart" ref={containerRef}>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={'0 0 ' + W + ' ' + H}
        preserveAspectRatio="xMidYMid meet"
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        onMouseMove={handlePointerMove}
        onMouseDown={handleSvgMouseDown}
        onMouseLeave={() => { setHoveredIdx(null); }}
        style={{ cursor: zoom > 1.1 ? 'grab' : 'default', userSelect: 'none' }}
      >
        <g transform={transform}>
          {renderGrid()}
          <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#333" strokeWidth={2} />
          <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#333" strokeWidth={2} />
          {renderAxisLabels()}
          {pathD ? <path d={pathD} fill="none" stroke="#007bff" strokeWidth={2} /> : null}
          {renderPoints()}
        </g>
      </svg>
      <div className="mt-2 d-flex gap-2 flex-wrap align-items-center">
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={addPoint}>+ Add Point</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetToDefaults}>Reset to Defaults</button>
        {(zoom > 1.05 || panX !== 0 || panY !== 0) && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleResetView}>Reset View</button>
        )}
        <span className="ms-auto small text-muted">Zoom: {(zoom * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}

export { TimePenaltyChart, GLOBAL_TIME_PENALTY_DEFAULTS };
